import React, { useRef, useEffect, useCallback } from 'react';
import { Play, Calendar, Globe, Languages, UtensilsCrossed } from 'lucide-react';
import RotatingText from './RotatingText';
import RetellContactsDemo from './RetellContactsDemo';
import './Hero.css';

const Hero = () => {
  const phoneRef = useRef(null);
  const demoRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const floater = entry.target.querySelector('.phone-floater');
          if (floater) floater.classList.add('in-view');
        }
      },
      { threshold: 0.2 }
    );

    if (phoneRef.current) observer.observe(phoneRef.current);
    return () => observer.disconnect();
  }, []);

  const handleTryIt = useCallback(() => {
    if (demoRef.current) demoRef.current.tryFirstAgent();

    const floater = phoneRef.current?.querySelector('.phone-floater');
    if (floater) {
      floater.classList.add('phone-attention');
      setTimeout(() => floater.classList.remove('phone-attention'), 1200);
    }
  }, []);

  return (
    <section className="hero-section" id="hero">
      <div className="hero-container">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              <span className="title-text">AI Agents That </span>
              <RotatingText words={['Never Miss a Call', 'Book Appointments', 'Take Orders', 'Speak Any Language']} />
            </h1>
            <p className="hero-subtitle">
              Your AI receptionist that handles orders, bookings, and speaks any language — 24/7. Built for businesses that never want to miss a call.
            </p>
            <button className="hero-cta-hint" onClick={handleTryIt}>
              <Play size={16} /> Try it now &rarr; Call any agent in the phone
            </button>
          </div>
          <div className="hero-phone" id="phone" ref={phoneRef}>
            {/* Top CTA — "‹ TRY IT NOW ›" with bouncing chevrons */}
            <button className="phone-try-cta" onClick={handleTryIt} type="button">
              <span className="try-chev try-chev-l" aria-hidden="true">&lsaquo;</span>
              <span className="try-text">Try it now</span>
              <span className="try-chev try-chev-r" aria-hidden="true">&rsaquo;</span>
            </button>

            {/* Aurora orb (huge conic gradient) */}
            <div className="aurora-orb" aria-hidden="true"></div>

            {/* Secondary teal glow */}
            <div className="secondary-glow" aria-hidden="true"></div>

            {/* Phone-shaped signal waves radiating outward */}
            <div className="signal-wave sw-1" aria-hidden="true"></div>
            <div className="signal-wave sw-2" aria-hidden="true"></div>
            <div className="signal-wave sw-3" aria-hidden="true"></div>

            {/* Floating sparkle dots */}
            <span className="sparkle sp-1" aria-hidden="true"></span>
            <span className="sparkle sp-2" aria-hidden="true"></span>
            <span className="sparkle sp-3" aria-hidden="true"></span>
            <span className="sparkle sp-4" aria-hidden="true"></span>
            <span className="sparkle sp-5" aria-hidden="true"></span>
            <span className="sparkle sp-6" aria-hidden="true"></span>

            {/* Floating feature tags */}
            <div className="floating-tag tag-1" aria-hidden="true">
              <Globe size={13} /> 24/7 Voice Agent
            </div>
            <div className="floating-tag tag-2" aria-hidden="true">
              <Languages size={13} /> Multi-Lingual
            </div>
            <div className="floating-tag tag-3" aria-hidden="true">
              <Calendar size={13} /> Books appointments
            </div>
            <div className="floating-tag tag-4" aria-hidden="true">
              <UtensilsCrossed size={13} /> Unlimited Orders
            </div>

            {/* The phone — gradient border ring + iPhone-style body with side buttons */}
            <div className="phone-floater">
              <div className="phone-border-ring" aria-hidden="true"></div>
              <div className="phone-body">
                {/* Left side: action button, volume up, volume down */}
                <span className="phone-btn phone-btn-action" aria-hidden="true"></span>
                <span className="phone-btn phone-btn-vol-up" aria-hidden="true"></span>
                <span className="phone-btn phone-btn-vol-down" aria-hidden="true"></span>
                {/* Right side: side / power button */}
                <span className="phone-btn phone-btn-power" aria-hidden="true"></span>

                <div className="phone-notch" aria-hidden="true">
                  <span className="phone-camera"></span>
                </div>
                <div className="phone-screen">
                  <RetellContactsDemo ref={demoRef} showNotification={false} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
